import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '../../../../../components/Button';
import { Modal, type ModalSize } from '../../../../../components/Modal';
import { ApiError } from '../../../../../lib/apiError';
import { focusValidationError } from '../../../../../lib/validationFocus';
import { personnelApi } from '../../personnelApi';
import type { PersonnelDetail, UpdatePersonnelRequest } from '../../personnelTypes';
import { toUpdateRequest } from '../../utils/toUpdateRequest';
import { PersonnelSectionContext } from './sectionForm';

/**
 * The shell every personnel section editor sits in.
 *
 * Each profile card has its **own** modal over its **own** fields — the panels are the create
 * wizard's steps, so a field is corrected where it was entered. What none of them own is the
 * plumbing: the confirm tick, the footer, the error banner, the save call, and sending back
 * the sections the reader did not come to change. Five copies of that is five chances for one
 * of them to blank a field it forgot, which is exactly what `toUpdateRequest` exists to stop.
 *
 * **It does not send the identity block unless a section asks it to.** `toUpdateRequest` omits
 * `firstName`, `lastName` and `dateOfBirth` entirely, so `rsys_pii` is untouched by default
 * and a section correcting an address needs `personnel.update` alone.
 *
 * `<EditPersonalModal>` is the ONE section that puts them back, from its own `build()`, and
 * only for a reader who could reveal them in the first place — the name belongs on the card
 * that shows the person's details, so it is edited there rather than through a second entry in
 * the same cog. A third section doing this must say so as loudly.
 */
export function PersonnelSectionModal({
  open,
  record,
  title,
  idPrefix,
  size = 'lg',
  onClose,
  onSaved,
  onOpen,
  onGenerate,
  build,
  valid = true,
  children,
}: {
  open: boolean;
  record: PersonnelDetail;
  title: string;
  /** Prefixes every id in the form, and is what `focusValidationError` maps a field onto. */
  idPrefix: string;
  /** `sm` is reserved for confirmations — a form is `lg` or wider. */
  size?: ModalSize;
  onClose: () => void;
  onSaved: () => void;
  /** Seeds the section's fields from the record. Runs on every open, never on every render. */
  onOpen: () => void;
  /**
   * Fills this section with sample data — the same Autogenerate the create form carries.
   *
   * On an EDIT form, which used to be forbidden and is not any more (product call,
   * 14 Sep 2026): the form most often needing filling fast is the one over a section that was
   * never collected. It overwrites what is there, and the confirm tick is what stops that
   * reaching the server by accident.
   */
  onGenerate: () => void;
  /** This section's fields. Merged over the record's other sections, which travel unchanged. */
  build: () => Partial<UpdatePersonnelRequest>;
  /**
   * Whether this section's own fields are complete.
   *
   * Defaults true because most sections are all-optional. A section with a REQUIRED field
   * passes it — otherwise the tick alone would let an empty value through and the server
   * would be the first thing to notice, which is the rejection the form is supposed to
   * prevent rather than provoke.
   */
  valid?: boolean;
  children: ReactNode;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    onOpen();
    setConfirmed(false);
    setError(null);
    // `onOpen` is a fresh closure every render; depending on it would re-seed the form on
    // every keystroke and throw away what the reader had typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      /*
        The whole record, then this section over the top. The endpoint replaces what it is
        sent, so the sections the reader did not open have to travel back unchanged — and the
        identity block has to stay absent, or the server reads the request as a name change
        and demands a name this page never loaded.
      */
      await personnelApi.update(record.id, { ...toUpdateRequest(record), ...build() });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this section.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size={size}
      id={`${idPrefix}-modal`}
      autogenerate={{ onGenerate, disabled: saving }}
      confirm={{
        id: `${idPrefix}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${idPrefix}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${idPrefix}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The section's fields. `saving` and `error` reach them through the render prop. */}
        <PersonnelSectionContext.Provider value={{ saving, error }}>
          {children}
        </PersonnelSectionContext.Provider>
      </div>
    </Modal>
  );
}
